import {
  CheckIcon,
  PencilSquareIcon,
  PhotoIcon,
  SpeakerWaveIcon,
  SparklesIcon,
  TrashIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { ResourceStatus, ResourceType, type IVideoClipResource } from '@packages/global';
import { Handle, Position } from '@xyflow/react';
import type { ComponentType, MouseEvent, SVGProps } from 'react';

import AudioPlayer from '../../../common/AudioPlayer';
import CanvasPreviewImage from './CanvasPreviewImage';
import type { CandidateResourceNodeData, ClipResourceGroupType } from '../types';

const STATUS_LABELS: Record<string, string> = {
  pending: '等待中',
  submitted: '已提交',
  processing: '生成中',
  generating: '生成中',
  completed: '已完成',
  failed: '失败',
};

const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
  [ResourceType.START_IMAGE]: '开始帧',
  [ResourceType.END_IMAGE]: '结束帧',
  [ResourceType.STORYBOARD_IMAGE]: '分镜图',
  [ResourceType.VIDEO]: '视频',
  [ResourceType.SCRIPT_AUDIO]: '配音',
  [ResourceType.SOUND_EFFECT_AUDIO]: '音效',
  [ResourceType.LIP_SYNC_VIDEO]: '对口型视频',
  [ResourceType.AUDIO_EDIT]: '音频编辑',
};

const GROUP_TYPE_LABELS: Record<ClipResourceGroupType, string> = {
  image: '画面',
  audio: '配音',
  video: '视频',
};

const GROUP_ICONS: Record<ClipResourceGroupType, ComponentType<SVGProps<SVGSVGElement>>> = {
  image: PhotoIcon,
  audio: SpeakerWaveIcon,
  video: VideoCameraIcon,
};

interface CandidateResourceNodeProps {
  data: CandidateResourceNodeData;
}

function getResourceMediaType(resourceType: ResourceType): ClipResourceGroupType {
  if (
    resourceType === ResourceType.SCRIPT_AUDIO
    || resourceType === ResourceType.SOUND_EFFECT_AUDIO
    || resourceType === ResourceType.AUDIO_EDIT
  ) {
    return 'audio';
  }
  if (resourceType === ResourceType.VIDEO || resourceType === ResourceType.LIP_SYNC_VIDEO) {
    return 'video';
  }
  return 'image';
}

function getResourceTaskId(resource: IVideoClipResource): string {
  return resource.imageGenerationId
    || resource.imageEditId
    || resource.videoGenerationId
    || resource.audioGenerationId
    || resource.id;
}

function getPrompt(resource: IVideoClipResource): string | undefined {
  try {
    const params = typeof resource.generationParams === 'string'
      ? JSON.parse(resource.generationParams)
      : resource.generationParams;
    const prompt = params?.prompt || params?.text;
    return typeof prompt === 'string' && prompt.trim() ? prompt : undefined;
  } catch {
    return undefined;
  }
}

function hasGenerationParams(resource: IVideoClipResource): boolean {
  if (!resource.generationParams) return false;
  if (typeof resource.generationParams === 'string') {
    return Boolean(resource.generationParams.trim());
  }
  return true;
}

function stopPropagation(event: MouseEvent<HTMLButtonElement>) {
  event.stopPropagation();
}

function getResourceTimestamp(resource: IVideoClipResource): number {
  const value = resource.createdAt ?? resource.updatedAt;
  const timestamp = value ? new Date(value).getTime() : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function isResourceLoading(resource: IVideoClipResource): boolean {
  const status = String(resource.status);
  return status === ResourceStatus.PENDING
    || status === ResourceStatus.GENERATING
    || status === 'submitted'
    || status === 'generating';
}

export default function CandidateResourceNode({ data }: CandidateResourceNodeProps) {
  const GroupIcon = GROUP_ICONS[data.groupType];
  const sortedResources = [...data.resources].sort((a, b) => getResourceTimestamp(b) - getResourceTimestamp(a));

  return (
    <div className="clip-canvas-node clip-canvas-node--resource">
      <Handle type="target" position={Position.Left} />

      <div className="clip-canvas-node__header clip-canvas-node__header--compact">
        <GroupIcon className="clip-canvas-node__icon" />
        <span>{data.label}</span>
        <span className="clip-canvas-resource-count">{data.resources.length}</span>
      </div>

      {data.resources.length === 0 ? (
        <div className="clip-canvas-resource-empty">
          <SparklesIcon className="clip-canvas-resource-empty__icon" />
          <span>暂无{GROUP_TYPE_LABELS[data.groupType]}候选</span>
        </div>
      ) : (
        <div className="clip-canvas-resource-list">
          {sortedResources.map((resource) => {
            const mediaType = getResourceMediaType(resource.resourceType);
            const statusLabel = STATUS_LABELS[resource.status] ?? resource.status;
            const prompt = getPrompt(resource);
            const canCreateForm = hasGenerationParams(resource);
            const isCompleted = resource.status === ResourceStatus.COMPLETED && Boolean(resource.resultUrl);
            const isLoading = isResourceLoading(resource);
            const canUseAsReference = isCompleted && (mediaType === 'image' || mediaType === 'audio');
            const referenceHandleClass = mediaType === 'audio'
              ? 'clip-canvas-resource-item__handle clip-canvas-resource-item__handle--audio'
              : 'clip-canvas-resource-item__handle clip-canvas-resource-item__handle--image';

            return (
              <div key={resource.id} className="clip-canvas-resource-item">
                {canUseAsReference && (
                  <Handle
                    id={`reference:${mediaType}:${resource.id}`}
                    type="source"
                    position={Position.Right}
                    className={referenceHandleClass}
                    title={mediaType === 'audio' ? '使用这个音频' : '使用这张图片'}
                  />
                )}
                {isLoading && (
                  <div className="clip-canvas-resource-item__loading" aria-label={statusLabel}>
                    <span className="clip-canvas-resource-item__spinner" />
                    <span>{statusLabel}</span>
                  </div>
                )}
                <div className="clip-canvas-resource-item__header">
                  <div className="clip-canvas-resource-item__title">
                    <span>{RESOURCE_TYPE_LABELS[resource.resourceType]}</span>
                    <span className="clip-canvas-resource-item__id">{getResourceTaskId(resource)}</span>
                  </div>
                  <span className={`clip-canvas-status clip-canvas-status--${resource.status}`}>
                    {statusLabel}
                  </span>
                </div>

                <div className={`clip-canvas-resource-preview clip-canvas-resource-preview--${mediaType}`}>
                  <div className="clip-canvas-resource-overlay">
                    {canCreateForm && (
                      <button
                        type="button"
                        className="clip-canvas-resource-overlay__button"
                        title="复用参数创建表单"
                        aria-label="复用参数创建表单"
                        onClick={(event) => {
                          stopPropagation(event);
                          void data.actions.onCreateFormFromResource?.(resource);
                        }}
                      >
                        <PencilSquareIcon className="clip-canvas-node__button-icon" />
                      </button>
                    )}
                    <button
                      type="button"
                      className="clip-canvas-resource-overlay__button clip-canvas-resource-overlay__button--danger"
                      title="删除"
                      aria-label="删除候选资源"
                      onClick={(event) => {
                        stopPropagation(event);
                        void data.actions.onDeleteResource?.(resource.id);
                      }}
                    >
                      <TrashIcon className="clip-canvas-node__button-icon" />
                    </button>
                  </div>

                  {resource.resultUrl && mediaType === 'image' && (
                    <CanvasPreviewImage
                      src={resource.resultUrl}
                      alt={RESOURCE_TYPE_LABELS[resource.resourceType]}
                      imageClassName="clip-canvas-preview__media"
                    />
                  )}
                  {resource.resultUrl && mediaType === 'audio' && (
                    <div className="clip-canvas-resource-preview__audio nodrag nopan nowheel">
                      <AudioPlayer src={resource.resultUrl} className="w-full h-8" />
                    </div>
                  )}
                  {resource.resultUrl && mediaType === 'video' && (
                    <video
                      src={resource.resultUrl}
                      className="clip-canvas-preview__media nodrag nopan nowheel"
                      controls
                      preload="metadata"
                    />
                  )}
                  {!resource.resultUrl && !isLoading && (
                    <div className="clip-canvas-resource-preview__empty">
                      {resource.errorMessage || '暂无结果'}
                    </div>
                  )}
                </div>

                {prompt && (
                  <div className="clip-canvas-resource-item__prompt">
                    {prompt}
                  </div>
                )}

                <div className="clip-canvas-node__actions clip-canvas-resource-actions">
                  {isCompleted && mediaType === 'image' && (
                    <>
                      <button
                        type="button"
                        className="clip-canvas-node__button"
                        onClick={(event) => {
                          stopPropagation(event);
                          void data.actions.onApplyImageResource?.(resource.resultUrl!, 'start');
                        }}
                      >
                        <CheckIcon className="clip-canvas-node__button-icon" />
                        应用开始帧
                      </button>
                      <button
                        type="button"
                        className="clip-canvas-node__button"
                        onClick={(event) => {
                          stopPropagation(event);
                          void data.actions.onApplyImageResource?.(resource.resultUrl!, 'end');
                        }}
                      >
                        <CheckIcon className="clip-canvas-node__button-icon" />
                        应用结束帧
                      </button>
                      <button
                        type="button"
                        className="clip-canvas-node__button"
                        onClick={(event) => {
                          stopPropagation(event);
                          void data.actions.onApplyImageResource?.(resource.resultUrl!, 'storyboard');
                        }}
                      >
                        <CheckIcon className="clip-canvas-node__button-icon" />
                        应用分镜图
                      </button>
                    </>
                  )}

                  {isCompleted && mediaType === 'audio' && (
                    <button
                      type="button"
                      className="clip-canvas-node__button"
                      onClick={(event) => {
                        stopPropagation(event);
                        void data.actions.onApplyAudioResource?.(resource.resultUrl!);
                      }}
                    >
                      <CheckIcon className="clip-canvas-node__button-icon" />
                      应用配音
                    </button>
                  )}

                  {isCompleted && mediaType === 'video' && (
                    <button
                      type="button"
                      className="clip-canvas-node__button"
                      onClick={(event) => {
                        stopPropagation(event);
                        void data.actions.onApplyVideoResource?.(resource.resultUrl!);
                      }}
                    >
                      <CheckIcon className="clip-canvas-node__button-icon" />
                      应用视频
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
